// Central source of truth for all SEO metadata.
// Consumed by <Seo /> at runtime and by scripts/generate-sitemap.js at build time.

import routes from './routes.json';
import { LOCATIONS } from './locations';

export const SITE_URL = routes.siteUrl;
export const SITE_NAME = 'Lavinia Lee Music Studio';
export const CONTACT_EMAIL = 'lavinialeemusicstudio@gmail.com';
export const BOOKING_URL = 'https://calendly.com/lavinialeemusicstudio/';
export const OG_IMAGE = `${SITE_URL}/og-image.jpg`;

// North Shore suburbs served in person. Mirrors LocationSection.jsx.
export const SERVICE_AREAS = [
  { name: 'Northbrook', lat: 42.1275, lng: -87.8289 },
  { name: 'Glenview', lat: 42.0723, lng: -87.815 },
  { name: 'Northfield', lat: 42.1011, lng: -87.7712 },
  { name: 'Lake Forest', lat: 42.2586, lng: -87.8406 },
  { name: 'Winnetka', lat: 42.1081, lng: -87.7359 },
  { name: 'Wilmette', lat: 42.0723, lng: -87.7228 },
];

// Canonical English paths. The Chinese versions live at /tw + path.
// Shared with scripts/prerender.js and scripts/generate-sitemap.js.
export const ROUTES = routes.canonical;

// Titles stay under ~60 characters so Google doesn't truncate them.
// Every title carries at least one instrument keyword and one location keyword.
export const META = {
  '/': {
    en: {
      title: 'Piano, Violin & Viola Lessons — Northbrook & Glenview IL',
      description:
        "Private piano, violin, and viola lessons on Chicago's North Shore. Serving Northbrook, Glenview, Northfield, Winnetka, Wilmette, and Lake Forest, plus online lessons across Chicagoland.",
    },
    tw: {
      title: '芝加哥北岸鋼琴、小提琴與中提琴課程 | Northbrook・Glenview',
      description:
        '芝加哥北岸私人鋼琴、小提琴與中提琴教學。服務 Northbrook、Glenview、Northfield、Winnetka、Wilmette 與 Lake Forest 等地區，並提供大芝加哥地區線上課程。',
    },
  },
  '/about': {
    en: {
      title: 'About Lavinia Lee — Piano & Violin Teacher, North Shore IL',
      description:
        'Meet Lavinia Lee, a private piano, violin, and viola teacher serving Chicago\'s North Shore. Learn about her teaching philosophy, training, and studio approach for students of all ages.',
    },
    tw: {
      title: '關於 Lavinia Lee — 芝加哥北岸鋼琴與小提琴老師',
      description:
        '認識 Lavinia Lee 老師，於芝加哥北岸提供私人鋼琴、小提琴與中提琴教學。了解她的教學理念、專業訓練與適合各年齡層學生的教學方式。',
    },
  },
  '/piano': {
    en: {
      title: 'Piano Lessons in Northbrook & Glenview, IL | Lavinia Lee',
      description:
        'Private piano lessons for all ages and levels on the North Shore. Personalized curriculum, music theory, recitals, and competition preparation in Northbrook, Glenview, Winnetka, and Wilmette.',
    },
    tw: {
      title: 'Northbrook・Glenview 鋼琴課程 | Lavinia Lee 音樂教室',
      description:
        '芝加哥北岸私人鋼琴課程，適合各年齡與程度的學生。量身打造的課程內容、樂理教學、成果發表會與比賽指導，服務 Northbrook、Glenview、Winnetka 與 Wilmette。',
    },
  },
  '/violin-viola': {
    en: {
      title: 'Violin & Viola Lessons — Northbrook & Glenview, IL',
      description:
        'Private violin and viola lessons on Chicago\'s North Shore. Build intonation, tone, and technique with lessons tailored to beginners through advanced competition and audition repertoire.',
    },
    tw: {
      title: 'Northbrook・Glenview 小提琴與中提琴課程 | Lavinia Lee',
      description:
        '芝加哥北岸私人小提琴與中提琴課程。從初學到進階比賽與甄選曲目，循序建立音準、音色與演奏技巧，課程依學生程度量身規劃。',
    },
  },
  '/current-students': {
    en: {
      title: 'Student Resources | Lavinia Lee Music Studio',
      description:
        'Practice guides, studio policies, recital information, and lesson resources for current piano, violin, and viola students at Lavinia Lee Music Studio.',
    },
    tw: {
      title: '學生資源 | Lavinia Lee 音樂教室',
      description:
        'Lavinia Lee 音樂教室在學學生專區：練習指南、教室規定、成果發表會資訊與課程相關資源。',
    },
  },
  '/upcoming-events': {
    en: {
      title: 'Fall 2026 Piano & Violin Recitals — Northfield, IL',
      description:
        'Upcoming events at Lavinia Lee Music Studio: the Fall 2026 Piano Recital on November 7 and Violin Recital on November 8, 1:30 PM, at 550 Sunset Ridge Rd, Northfield. Free admission.',
    },
    tw: {
      title: '2026 秋季鋼琴與小提琴發表會 | Lavinia Lee 音樂教室',
      description:
        'Lavinia Lee 音樂教室近期活動：2026 秋季鋼琴發表會（11 月 7 日）與小提琴發表會（11 月 8 日），下午 1:30，地點 550 Sunset Ridge Rd, Northfield，免費入場。',
    },
  },
  '/contact': {
    en: {
      title: 'Free Trial Lesson — Piano & Violin, Northbrook IL',
      description:
        'Book a free trial lesson for piano, violin, or viola on Chicago\'s North Shore. Serving Northbrook, Glenview, Northfield, Winnetka, Wilmette, and Lake Forest.',
    },
    tw: {
      title: '免費體驗課・鋼琴與小提琴 | Northbrook 音樂教室',
      description:
        '預約免費體驗課，了解芝加哥北岸的鋼琴、小提琴與中提琴課程。服務 Northbrook、Glenview、Northfield、Winnetka、Wilmette 與 Lake Forest。',
    },
  },
};

// Per-suburb landing pages get their metadata from locations.js so the copy and
// the <title>/description live in one place.
Object.values(LOCATIONS).forEach((loc) => {
  META[`/${loc.slug}`] = {
    en: { title: loc.title.en, description: loc.description.en },
    tw: { title: loc.title.tw, description: loc.description.tw },
  };
});

// Strips the /tw prefix and normalises to a key in META.
export function canonicalPath(pathname) {
  const stripped = pathname.replace(/^\/tw(?=\/|$)/, '') || '/';
  const noTrailing = stripped.length > 1 ? stripped.replace(/\/$/, '') : stripped;
  return META[noTrailing] ? noTrailing : '/';
}

export function langFromPath(pathname) {
  return /^\/tw(\/|$)/.test(pathname) ? 'tw' : 'en';
}

// Pages with nothing useful for a search engine — currently the password-gated
// student area, whose prerendered form is all a crawler would ever see.
export const NOINDEX = routes.noindex || [];

export function metaFor(pathname) {
  const key = canonicalPath(pathname);
  const lang = langFromPath(pathname);
  return { ...META[key][lang], key, lang, noindex: NOINDEX.includes(key) };
}

// Absolute URLs for a canonical key in each language.
//
// Trailing slashes are deliberate: the host serves /piano/ and 301s /piano to
// it, so a canonical without the slash points at a URL that redirects.
export function urlsFor(key) {
  const path = key === '/' ? '/' : `${key}/`;
  return {
    en: `${SITE_URL}${path}`,
    tw: `${SITE_URL}/tw${path}`,
  };
}

// BreadcrumbList tells Google the site hierarchy and lets it render a path
// instead of a bare URL under the result title.
export function breadcrumbSchema(key, lang, title) {
  const urls = urlsFor(key);
  const home = lang === 'tw' ? `${SITE_URL}/tw/` : `${SITE_URL}/`;
  const items = [{ '@type': 'ListItem', position: 1, name: SITE_NAME, item: home }];
  if (key !== '/') {
    items.push({ '@type': 'ListItem', position: 2, name: title, item: urls[lang] });
  }
  return { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: items };
}

// One Service entry per instrument, scoped to the towns actually served.
export function serviceSchema(instrument) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: `Private ${instrument} Lessons`,
    provider: { '@id': `${SITE_URL}/#studio` },
    areaServed: SERVICE_AREAS.map((a) => ({ '@type': 'City', name: a.name })),
    audience: { '@type': 'Audience', audienceType: 'Students of all ages and levels' },
    availableChannel: {
      '@type': 'ServiceChannel',
      serviceUrl: BOOKING_URL,
      name: 'Book a free trial lesson',
    },
  };
}

// Student performance videos are real content Google can surface as video
// results. Parsed out of the recital lists the instrument pages already render.
export function videoSchema(items) {
  const entries = items
    .map((item) => {
      const text = String(item.label || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
      if (!text || !item.video) return null;
      return {
        '@type': 'VideoObject',
        name: `${text} — ${SITE_NAME}`,
        description: `Student performance: ${text}, taught at ${SITE_NAME} on Chicago's North Shore.`,
        thumbnailUrl: OG_IMAGE,
        contentUrl: item.video,
        uploadDate: '2025-08-01',
      };
    })
    .filter(Boolean);
  return entries.length
    ? { '@context': 'https://schema.org', '@graph': entries }
    : null;
}

// LocalBusiness structured data. Rendered once, on the home page.
export function localBusinessSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': ['MusicSchool', 'LocalBusiness'],
    '@id': `${SITE_URL}/#studio`,
    name: SITE_NAME,
    url: SITE_URL,
    email: CONTACT_EMAIL,
    image: OG_IMAGE,
    logo: `${SITE_URL}/owl-logo.png`,
    description:
      "Private piano, violin, and viola lessons for all ages on Chicago's North Shore, with online lessons available across Chicagoland.",
    priceRange: '$$',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Northbrook',
      addressRegion: 'IL',
      addressCountry: 'US',
    },
    areaServed: SERVICE_AREAS.map((area) => ({
      '@type': 'City',
      name: area.name,
      address: {
        '@type': 'PostalAddress',
        addressLocality: area.name,
        addressRegion: 'IL',
        addressCountry: 'US',
      },
    })),
    knowsLanguage: ['en', 'zh-Hant'],
    founder: {
      '@type': 'Person',
      name: 'Lavinia Lee',
      jobTitle: 'Music Instructor',
      description:
        'Violin, viola, and piano teacher with over 20 years of teaching and performing experience, blending Suzuki method, music theory, and orchestral training.',
      alumniOf: [
        { '@type': 'CollegeOrUniversity', name: 'Yale University' },
        { '@type': 'CollegeOrUniversity', name: 'Northwestern University' },
      ],
      knowsAbout: ['Piano', 'Violin', 'Viola', 'Suzuki method', 'Music theory', 'Competition preparation'],
    },
    potentialAction: {
      '@type': 'ReserveAction',
      name: 'Book a Free Trial Lesson',
      target: BOOKING_URL,
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Private Music Lessons',
      itemListElement: [
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Private Piano Lessons' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Private Violin Lessons' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Private Viola Lessons' } },
      ],
    },
  };
}
